/**
 * Helpers e Vue mixin per le frecce custom di navigazione anno sui
 * <v-date-picker> che espongono uno scoped slot #header.
 *
 * Problema: page.move(±12) di v-calendar viene silenziosamente rigettato
 * quando il mese target è interamente fuori da min/max, lasciando la freccia
 * "inerte" ai bordi dell'anno. Inoltre, un predicato ingenuo come
 * `page.year <= parseInt(min)` confronta solo l'anno e ignora la precisione a
 * livello di mese del bound.
 *
 * Le funzioni pure implementano un clamp consapevole del mese:
 *   - move_picker() posiziona il picker sul primo/ultimo mese valido quando
 *     l'anno target è solo parzialmente dentro il range.
 *   - is_at_year_bound() disabilita la freccia solo quando un click non
 *     produrrebbe spostamento visibile (clamp result == page corrente).
 *
 * Il mixin `calendar_mixin` espone queste funzioni come methods e aggiunge
 * `move_datepicker(ref_name, ...)` che risolve `this.$refs[ref_name]` prima
 * di delegare a `move_picker`.
 *
 * Contratto verso il consumer:
 *   - `ref_name` — chiave stringa del ref del picker sul componente host
 *     (es. 'datepicker_' + index in LF, 'datepicker:' + pIndex in VV).
 *   - `page` — l'oggetto fornito dallo scoped slot #header di v-calendar,
 *     con `{ year, month }` (month è 1-indexed).
 *   - `bounds` — `{ min, max }` stringhe in formato 'YYYY-MM-DD'.
 *
 * Utilizzo nel componente:
 *   import { calendar_mixin } from '@/utilities/calendar'
 *   export default {
 *     mixins: [calendar_mixin],
 *     ...
 *   }
 *
 * Le funzioni sono anche importabili singolarmente per unit test:
 *   import { clamp_year_target, is_at_year_bound, move_picker } from '@/utilities/calendar'
 *
 * Consumer attuali (al 2026-04-22):
 *   - src/sites/liveflight/components/blocks/details/travelers-details-form.vue
 *   - src/sites/alpitour/components/views/content-blocks/quote.vue
 */

/**
 * Calcola il (year, month) su cui atterrerebbe il click di una freccia
 * anno, clampando al mese più vicino dentro il range quando il mese
 * target è interamente fuori dai bound.
 *
 * @param {number} years_delta - -1 (indietro) o +1 (avanti)
 * @param {{year: number, month: number}} page - month è 1-indexed
 * @param {{min: string, max: string}} bounds - 'YYYY-MM-DD'
 * @returns {{year: number, month: number}}
 */
export function clamp_year_target(years_delta, page, bounds) {
  const min = new Date(bounds.min);
  const max = new Date(bounds.max);
  const target_year = page.year + years_delta;
  const target_month = page.month;
  const target_start = new Date(target_year, target_month - 1, 1);
  const target_end = new Date(target_year, target_month, 0);
  if (target_end < min) {
    return { year: min.getFullYear(), month: min.getMonth() + 1 };
  }
  if (target_start > max) {
    return { year: max.getFullYear(), month: max.getMonth() + 1 };
  }
  return { year: target_year, month: target_month };
}

/**
 * True quando un click sulla freccia anno non produrrebbe alcun cambio
 * di pagina visibile — usato per pilotare `:disabled` del bottone.
 *
 * @param {number} direction - -1 (‹) o +1 (›)
 */
export function is_at_year_bound(direction, page, bounds) {
  const target = clamp_year_target(direction, page, bounds);
  return target.year === page.year && target.month === page.month;
}

/**
 * Esegue il salto di navigazione anno sul picker passato, clampando al
 * primo mese valido quando l'anno target è parzialmente dentro il range.
 * No-op se il picker è falsy (componente non ancora montato).
 *
 * @param {{move: Function}} picker - v-calendar instance con .move()
 * @param {number} years_delta - -1 (indietro) o +1 (avanti)
 */
export function move_picker(picker, years_delta, page, bounds) {
  if (!picker) return;
  const target = clamp_year_target(years_delta, page, bounds);
  return picker.move({ month: target.month, year: target.year }).catch(() => {});
}

export const calendar_mixin = {
  methods: {
    clamp_year_target,
    is_at_year_bound,
    /**
     * Thin wrapper che risolve `this.$refs[ref_name]` e delega a move_picker.
     *
     * @param {string} ref_name - chiave del ref del picker sul componente host
     * @param {number} years_delta - -1 (indietro) o +1 (avanti)
     */
    move_datepicker(ref_name, years_delta, page, bounds) {
      const ref = this.$refs[ref_name];
      const picker = Array.isArray(ref) ? ref[0] : ref;
      return move_picker(picker, years_delta, page, bounds);
    },
  },
};
